import { route } from 'quasar/wrappers'
import type { RouterScrollBehavior } from 'vue-router'
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router'
import routes from './routes'
import { useAuthStore } from 'stores/auth-store'
import { useErrorStore } from 'stores/error-store'
import { Cookies } from 'quasar'
import { useLocaleStore } from 'src/stores/locale-store'
import { i18n } from 'boot/i18n'
/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let RouterInstance: any

export default route(function ({ ssrContext, store }) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory

  const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
    if (savedPosition) return savedPosition

    // Don't scroll when query changes but path is same
    if (to.path === from.path && to.fullPath !== from.fullPath) {
      return false // Cancel scroll
    }

    return { left: 0, top: 0 } // Default scroll to top
  }

  const Router = createRouter({
    scrollBehavior,
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  })

  Router.beforeEach((to, from, next) => {
    const excludedPaths = [
      '/robots.txt',
      '/sitemap.xml',
      '/favicon.ico',
      '/auth-callback',
      '/silent-check-sso.html',
    ]

    const cookies = process.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies

    if (excludedPaths.includes(to.path)) return next()
    if (to.name === 'Error') return next()

    if (to.path === '/') {
      const savedLang = cookies.get('lang') === 'el' ? 'el' : 'en'
      return next(`/${savedLang}`)
    }

    const authStore = useAuthStore(store)
    const localeStore = useLocaleStore(store)

    const langParam = to.params.lang as 'en' | 'el' | undefined

    if (langParam) {
      const appLocale = langParam === 'el' ? 'el-GR' : 'en-US'

      if (i18n.global.locale.value !== appLocale) {
        i18n.global.locale.value = appLocale
      }
      if (localeStore.locale !== appLocale) {
        localeStore.setLocale(appLocale, cookies)
      }
    }

    const requiresAuth = to.matched.some((record) => record.meta.isAuthenticated)
    const requiresAgent = to.matched.some((record) => record.meta.isAgent)
    const requiresApplicant = to.matched.some((record) => record.meta.isApplicant)
    const requiresActingAs = to.matched.some((record) => record.meta.actingAsRequired)
    const requiresProfile = to.matched.some((record) => record.meta.profileRequired)

    const fileRoute = to.matched.some((record) => record.meta.fileDownload)

    if (fileRoute) {
      if (!authStore.isAuthenticated) {
        if (process.env.CLIENT) {
          authStore.openLoginModal(cookies, to.path)
        } else {
          authStore.setPathToCookies(to.path, cookies)
        }
        if (!from.matched.length) {
          return next(`/${langParam || 'en'}`)
        }
        return next(from.path)
      }
      return next()
    }

    if (!requiresActingAs && authStore.actingAsUuid) authStore.setActingAs(null, cookies)

    if (requiresAuth) {
      if (!authStore.isAuthenticated) {
        if (process.env.CLIENT) {
          authStore.openLoginModal(cookies, to.fullPath)
        } else {
          authStore.setPathToCookies(to.fullPath, cookies)
        }
        if (!from.matched.length) {
          return next(`/${langParam || 'en'}`)
        }
        return next(from.path)
      }

      if (authStore.isEvaluator || authStore.isSecretary || authStore.isHelpdesk) {
        const errorStore = useErrorStore(store)
        errorStore.$patch({
          status: 403,
          error: 'Access Denied: You do not have the required permissions.',
        })
        return next({ name: 'Error', params: { lang: langParam } })
      }

      if (authStore.isApplicant && !requiresApplicant) {
        const errorStore = useErrorStore(store)
        errorStore.$patch({
          status: 403,
          error: 'Access Denied: You do not have the required permissions.',
        })
        return next({ name: 'Error', params: { lang: langParam } })
      }
      if (authStore.isAgent && !requiresAgent) {
        const errorStore = useErrorStore(store)
        errorStore.$patch({
          status: 403,
          error: 'Access Denied: You do not have the required permissions.',
        })
        return next({ name: 'Error', params: { lang: langParam } })
      }
      if (authStore.isApplicant && requiresProfile && !authStore.hasProfile)
        return next('/applicant-create?next=' + to.path)
      if (authStore.isAgent && requiresActingAs && !authStore.actingAsUuid)
        return next('/agent/applicants')
    }

    next()
  })
  return Router
})

export { RouterInstance as Router }
